import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  View,
  ScrollView,
  Text,
  Animated,
  PanResponder,
  TouchableOpacity,
  Platform,
} from "react-native";
import { useTranslation } from "react-i18next";
import Ionicons from "@expo/vector-icons/Ionicons";
import TextareaAutosize from "react-textarea-autosize";
import ChatMessage from "../Firestore/ChatMessage";
import EmojiMartPicker from "@emoji-mart/react";
import emojiMartData from "@emoji-mart/data";

import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";

import { Observer, observer } from "mobx-react";
import { audioFormatTime } from "../utils/numberUtils";
import ImageComponent from "../Components/ImageComponent";
import AudioManager from "../Classes/AudioManager";
import MediaManager from "../Classes/MediaManager";
import ChatManager from "../Classes/ChatManager";
import NetworkManager from "../Classes/NetworkManager";
import { useDevice } from "../_layout";
import { insertEmojiAtSelection } from "./chatEmojiTextUtils";
import {
  shouldPreventBlurOnEmojiTap,
  shouldRefocusAfterEmojiSelect,
  shouldRefocusAfterEmojiToggle,
  shouldRefocusAfterEmojiInteraction,
} from "./chatEmojiFocusPolicy";
import {
  buildImageSelection,
  extractImageFileFromClipboardEvent,
  extractImageFileFromInputEvent,
  isImageFile,
  resetFileInputValue,
  revokeImageSelection,
} from "./chatImageInputUtils";
import {
  applyForcedSelectionToTextarea,
  getTextareaSelection,
  shouldClearForcedSelection,
} from "./chatTextareaSelectionUtils";
import { ChatInputKeyAction, getChatInputKeyAction } from "./chatInputKeyUtils";
import { getChatEmojiPickerLayout } from "./chatEmojiPickerLayout";
import {
  CHAT_MESSAGE_TEXT_MAX_LENGTH,
  getTimestampForChatMessageSegment,
  limitChatMessageText,
  splitChatMessageText,
} from "./chatMessageSplitUtils";
import { useToast } from "../contexts/ToastContext";

const ChatInputView = observer(
  ({
    client,
    fullScreen = false,
    enableTypeToFocus = fullScreen,
    droppedImageDraft = null,
  }) => {
    const { t, i18n } = useTranslation();
    const { showToast } = useToast();
    const { isMobile, isIOS } = useDevice();
    const isWeb = Platform.OS === "web";
    const shouldShowAppEmojiButton = !(isMobile && isIOS);
    const inputFontSize = isMobile && isIOS ? 16 : 14;
    const inputLineHeight = inputFontSize + 4;
    const inputVerticalPadding = Math.max(0, (34 - inputLineHeight) / 2);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [forcedSelection, setForcedSelection] = useState(undefined);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const inputRef = useRef(null);
    const imageInputRef = useRef(null);
    const selectionRef = useRef({ start: 0, end: 0 });
    const emojiPickerPanelRef = useRef(null);
    const emojiPickerMountRef = useRef(null);
    const emojiToggleButtonRef = useRef(null);
    const sendButtonRef = useRef(null);
    const inputContainerRef = useRef(null);
    const isSendingMessageRef = useRef(false);
    const isSoftKeyboardOpenRef = useRef(false);
    const wasInputFocusedOnEmojiTapRef = useRef(false);

    const chatManager = ChatManager.getInstance();
    const audioManager = AudioManager.getInstance();
    const mediaManager = MediaManager.getInstance();
    const pickerLocale = (i18n?.language || "en").toLowerCase().startsWith("es")
      ? "es"
      : "en";
    const emojiPickerLayout = getChatEmojiPickerLayout({ isMobile });
    const preventBlurOnEmojiTap = shouldPreventBlurOnEmojiTap({
      isWeb,
      isMobile,
    });
    const refocusAfterEmojiInteraction = shouldRefocusAfterEmojiInteraction({
      isWeb,
      isMobile,
    });

    const focusChatInput = useCallback(() => {
      const input = inputRef.current;
      if (!input?.focus) return;
      try {
        input.focus({ preventScroll: true });
      } catch {
        input.focus();
      }
    }, []);

    const blurChatInput = useCallback(() => {
      const input = inputRef.current;
      if (!input?.blur) return;
      try {
        input.blur();
      } catch {
        // no-op
      }
    }, []);

    const getIsSoftKeyboardOpenNow = useCallback(() => {
      if (typeof window === "undefined") return isSoftKeyboardOpenRef.current;

      const vv = window.visualViewport;
      if (!vv) return isSoftKeyboardOpenRef.current;

      const diff = (window.innerHeight || 0) - (vv.height || 0);
      const isOpen = diff > 120;
      isSoftKeyboardOpenRef.current = isOpen;
      return isOpen;
    }, []);

    const focusChatInputAtEnd = useCallback(() => {
      focusChatInput();

      const textLength = (client.chatText || "").length;
      const endSelection = { start: textLength, end: textLength };
      const didApply = applyForcedSelectionToTextarea(
        inputRef.current,
        endSelection,
      );

      selectionRef.current = endSelection;
      if (!didApply) {
        setForcedSelection(endSelection);
      } else {
        setForcedSelection(undefined);
      }
    }, [client.chatText, focusChatInput]);

    const closeChatPanel = useCallback(() => {
      setShowEmojiPicker(false);
      client.coach.setProperty("selectedChatId", "");
      chatManager.setProperty("selectedReplyToId", null);
      mediaManager.closeMediaPlayer();
    }, [client, chatManager, mediaManager]);

    const clearSelectedImage = useCallback(() => {
      setSelectedImage(null);
    }, []);

    const showInvalidImageFileToast = useCallback(() => {
      showToast("selectImageFileText-string", {
        type: "error",
      });
    }, [showToast]);

    const showUploadImageErrorToast = useCallback(() => {
      showToast("uploadImageErrorTitle-string", {
        type: "error",
        description: "uploadImageErrorDescription-string",
      });
    }, [showToast]);

    const showSendMessageErrorToast = useCallback(() => {
      showToast("sendMessageErrorTitle-string", {
        type: "error",
        description: "sendMessageErrorDescription-string",
      });
    }, [showToast]);

    const selectImageDraft = useCallback(
      (file) => {
        if (!isImageFile(file)) {
          showInvalidImageFileToast();
          return false;
        }

        try {
          const nextSelection = buildImageSelection(file);
          setSelectedImage(nextSelection);
          return true;
        } catch (error) {
          console.error("Error preparing image preview:", error);
          showUploadImageErrorToast();
          return false;
        }
      },
      [showInvalidImageFileToast, showUploadImageErrorToast],
    );

    const sendMessage = async () => {
      const trimmedText = client.chatText.trim();
      if (trimmedText === "" && !selectedImage) return;

      if (isSendingMessageRef.current) return;
      isSendingMessageRef.current = true;
      setIsSendingMessage(true);

      try {
        setShowEmojiPicker(false);

        let textSegments;
        try {
          textSegments = splitChatMessageText(trimmedText);
        } catch (error) {
          console.error("Message is too long to send:", error);
          return;
        }

        // Cache the reply-to ID. Clear it only after the message is queued.
        const replyTo = chatManager.selectedReplyToId;
        const networkManager = NetworkManager.getInstance();
        const timestamp = networkManager.getNetworkTime();
        const createTextMessage = (segment, index) =>
          new ChatMessage(
            undefined,
            getTimestampForChatMessageSegment(timestamp, index),
            segment,
            "text",
            true,
            index === 0 && replyTo ? replyTo : null,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            client,
            undefined,
          );

        if (selectedImage) {
          try {
            const compressedImage = await mediaManager.compressImageFileWeb(
              selectedImage.file,
              Date.now().toString(),
            );

            if (!compressedImage?.file) {
              showUploadImageErrorToast();
              return;
            }

            const newMessage = new ChatMessage(
              undefined,
              timestamp,
              textSegments[0] ?? "",
              "image",
              true,
              replyTo ? replyTo : null,
              compressedImage.file,
              undefined,
              undefined,
              undefined,
              undefined,
              client,
              undefined,
            );
            const followUpMessages = textSegments
              .slice(1)
              .map((segment, offset) => createTextMessage(segment, offset + 1));
            await client.enqueueOutgoingChatMessages([
              newMessage,
              ...followUpMessages,
            ]);
            chatManager.scrollToBottom();
          } catch (error) {
            console.error("Error saving or uploading image:", error);
            showUploadImageErrorToast();
            return;
          }
        } else {
          // For text-only messages.
          const newMessages = textSegments.map((segment, index) =>
            createTextMessage(segment, index),
          );

          try {
            await client.enqueueOutgoingChatMessages(newMessages);
          } catch (error) {
            console.error("Error queueing text message:", error);
            showSendMessageErrorToast();
            return;
          }
          chatManager.scrollToBottom();
        }

        if (replyTo) {
          chatManager.setProperty("selectedReplyToId", null);
        }

        // Reset the text ref.
        // setText("");
        client.setProperty("chatText", "");

        selectionRef.current = { start: 0, end: 0 };
        setForcedSelection(undefined);
        setShowEmojiPicker(false);
        clearSelectedImage();
      } finally {
        isSendingMessageRef.current = false;
        setIsSendingMessage(false);
      }
    };

    const SendMessageButton = () => {
      return (
        <View ref={sendButtonRef} nativeID="chat-send-button">
          <TouchableOpacity
            style={{
              alignItems: "center",
              justifyContent: "center",
              paddingVertical: 6,
              borderRadius: 14,
              backgroundColor: Colors.messageBubbleColor,
              width: 54,
              height: isMobile ? 36 : 34,
              opacity: isSendingMessage ? 0.6 : 1,
            }}
            disabled={isSendingMessage}
            accessibilityState={{ disabled: isSendingMessage }}
            onPress={sendMessage}
          >
            <Ionicons
              name="paper-plane-sharp"
              size={20}
              color={Colors.whiteColor}
            />
          </TouchableOpacity>
        </View>
      );
    };

    const pickImage = useCallback(() => {
      imageInputRef.current?.click?.();
    }, []);

    const handleImageInputChange = useCallback(
      (event) => {
        const inputElement = event?.target ?? event?.currentTarget;
        const file = extractImageFileFromInputEvent(event);
        resetFileInputValue(inputElement);

        if (!file) return;

        selectImageDraft(file);
      },
      [selectImageDraft],
    );

    const handlePasteImage = useCallback(
      (event) => {
        if (!isWeb) return;

        const file = extractImageFileFromClipboardEvent(event);
        if (!file) return;

        event?.preventDefault?.();
        selectImageDraft(file);
      },
      [isWeb, selectImageDraft],
    );

    const handleEmojiButtonPress = () => {
      if (audioManager.isRecording) return;
      const isInputFocused =
        typeof document !== "undefined" &&
        document.activeElement === inputRef.current;
      const wasInputFocusedAtTap =
        wasInputFocusedOnEmojiTapRef.current || isInputFocused;
      wasInputFocusedOnEmojiTapRef.current = false;

      const shouldRefocusOnToggle = shouldRefocusAfterEmojiToggle({
        isWeb,
        isMobile,
        wasSoftKeyboardOpen: getIsSoftKeyboardOpenNow(),
      });

      if (!isWeb) {
        if (refocusAfterEmojiInteraction) {
          focusChatInput();
        }
        return;
      }

      if (showEmojiPicker) {
        setShowEmojiPicker(false);
        if (shouldRefocusOnToggle) {
          focusChatInput();
        } else if (preventBlurOnEmojiTap) {
          blurChatInput();
        }
        return;
      }

      setShowEmojiPicker(true);
      if (!shouldRefocusOnToggle && preventBlurOnEmojiTap) {
        blurChatInput();
        return;
      }

      if (refocusAfterEmojiInteraction) {
        const shouldFocusAtEndOnDesktopOpen =
          isWeb && !isMobile && !wasInputFocusedAtTap;

        setTimeout(() => {
          if (shouldFocusAtEndOnDesktopOpen) {
            focusChatInputAtEnd();
            return;
          }
          focusChatInput();
        }, 0);
      }
    };

    const handleEmojiSelect = (emojiEntry) => {
      const emoji = emojiEntry?.native;
      if (!emoji || audioManager.isRecording) return;

      const result = insertEmojiAtSelection(
        client.chatText,
        selectionRef.current,
        emoji,
      );

      client.setProperty("chatText", result.text);
      selectionRef.current = result.selection;
      setForcedSelection(result.selection);

      const shouldRefocusOnSelect = shouldRefocusAfterEmojiSelect({
        isWeb,
        isMobile,
        wasSoftKeyboardOpen: getIsSoftKeyboardOpenNow(),
      });

      if (!shouldRefocusOnSelect) return;

      if (preventBlurOnEmojiTap) {
        focusChatInput();
        return;
      }

      if (refocusAfterEmojiInteraction) {
        setTimeout(() => {
          focusChatInput();
        }, 0);
      }
    };

    const handleEmojiMouseDown = (event) => {
      if (typeof document !== "undefined") {
        wasInputFocusedOnEmojiTapRef.current =
          document.activeElement === inputRef.current;
      }
      if (!preventBlurOnEmojiTap) return;
      event?.preventDefault?.();
    };
    const emojiPanelBottomOffset = chatManager.selectedReplyToId ? 104 : 60;

    useEffect(() => {
      if (!preventBlurOnEmojiTap) return undefined;

      const vv = typeof window !== "undefined" ? window.visualViewport : null;
      if (!vv) return undefined;

      const update = () => {
        const diff = (window.innerHeight || 0) - (vv.height || 0);
        isSoftKeyboardOpenRef.current = diff > 120;
      };

      update();
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
      window.addEventListener("resize", update);

      return () => {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
      };
    }, [preventBlurOnEmojiTap]);

    useEffect(() => {
      if (!forcedSelection || !inputRef.current) return undefined;

      const applySelection = () => {
        const didApply = applyForcedSelectionToTextarea(
          inputRef.current,
          forcedSelection,
        );
        if (didApply) {
          selectionRef.current = forcedSelection;
          setForcedSelection(undefined);
        }
      };

      applySelection();
      const timerId = setTimeout(applySelection, 0);

      return () => clearTimeout(timerId);
    }, [forcedSelection, client.chatText]);

    useEffect(() => {
      if (!enableTypeToFocus) return undefined;

      const handleKeyDown = (e) => {
        // if user types the special literal 'scape', show a temporary placeholder
        if (
          typeof e.key === "string" &&
          e.key === "Escape" &&
          !audioManager.isRecording
        ) {
          closeChatPanel();
          return;
        }
        // only react to “real” characters (not Shift, Ctrl, etc.)
        const isInputFocused =
          typeof document !== "undefined" &&
          document.activeElement === inputRef.current;
        if (e.key.length === 1 && inputRef.current && !isInputFocused) {
          inputRef.current.focus();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [enableTypeToFocus, audioManager.isRecording, closeChatPanel]);

    useEffect(() => {
      if (audioManager.isRecording && showEmojiPicker) {
        setShowEmojiPicker(false);
      }
    }, [audioManager.isRecording, showEmojiPicker]);

    useEffect(() => {
      if (!droppedImageDraft?.file) return;
      selectImageDraft(droppedImageDraft.file);
    }, [droppedImageDraft, selectImageDraft]);

    useEffect(() => {
      if (!shouldShowAppEmojiButton && showEmojiPicker) {
        setShowEmojiPicker(false);
      }
    }, [shouldShowAppEmojiButton, showEmojiPicker]);

    useEffect(() => {
      if (!isWeb || !showEmojiPicker) return undefined;

      const mount = emojiPickerMountRef.current;
      if (!mount) return undefined;

      const syncEmojiHostToPanel = () => {
        mount.style.width = "100%";
        mount.style.height = "100%";
        mount.style.maxWidth = "100%";
        mount.style.maxHeight = "100%";
        mount.style.minHeight = "0";
        mount.style.overflow = "hidden";

        const pickerHost = mount.querySelector?.("em-emoji-picker");
        if (!pickerHost) return false;

        const widthPx =
          mount.clientWidth > 0 ? `${mount.clientWidth}px` : "100%";
        const heightPx =
          mount.clientHeight > 0 ? `${mount.clientHeight}px` : "100%";

        pickerHost.style.width = widthPx;
        pickerHost.style.height = heightPx;
        pickerHost.style.maxWidth = widthPx;
        pickerHost.style.maxHeight = heightPx;
        pickerHost.style.minHeight = "0";
        pickerHost.style.pointerEvents = "auto";
        return true;
      };

      if (syncEmojiHostToPanel()) return undefined;

      const observer =
        typeof MutationObserver !== "undefined"
          ? new MutationObserver(() => {
              if (syncEmojiHostToPanel()) {
                observer.disconnect();
              }
            })
          : null;

      observer?.observe(mount, { childList: true, subtree: true });
      const resizeObserver =
        typeof ResizeObserver !== "undefined"
          ? new ResizeObserver(() => {
              syncEmojiHostToPanel();
            })
          : null;
      resizeObserver?.observe(mount);

      const intervalId = setInterval(() => {
        if (syncEmojiHostToPanel()) {
          clearInterval(intervalId);
          observer?.disconnect();
        }
      }, 120);

      return () => {
        clearInterval(intervalId);
        observer?.disconnect();
        resizeObserver?.disconnect();
      };
    }, [isWeb, showEmojiPicker]);

    useEffect(() => {
      if (!isWeb || !showEmojiPicker || typeof document === "undefined") {
        return undefined;
      }

      const handleOutsidePress = (event) => {
        const panel = emojiPickerPanelRef.current;
        const toggleButton = emojiToggleButtonRef.current;
        const sendButton = sendButtonRef.current;
        const inputContainer = inputContainerRef.current;
        const target = event?.target;

        if (!panel) return;
        if (panel.contains?.(target)) return;
        if (
          toggleButton?.contains?.(target) ||
          target?.closest?.("#chat-emoji-toggle-button")
        ) {
          return;
        }
        if (sendButton?.contains?.(target) || target?.closest?.("#chat-send-button")) {
          return;
        }
        if (inputContainer?.contains?.(target)) return;
        setShowEmojiPicker(false);
      };

      document.addEventListener("mousedown", handleOutsidePress);
      document.addEventListener("touchstart", handleOutsidePress);

      return () => {
        document.removeEventListener("mousedown", handleOutsidePress);
        document.removeEventListener("touchstart", handleOutsidePress);
      };
    }, [isWeb, showEmojiPicker]);

    useEffect(() => {
      if (chatManager.selectedReplyToId != null) {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
      if (client && inputRef.current && !isMobile) {
        inputRef.current.focus();
      }
    }, [chatManager.selectedReplyToId, client]);

    useEffect(
      () => () => {
        revokeImageSelection(selectedImage);
      },
      [selectedImage],
    );

    return (
      <Animated.View style={{ zIndex: 5, position: "relative" }}>
        {isWeb && shouldShowAppEmojiButton && showEmojiPicker && (
          <View
            style={{
              position: "absolute",
              left: 10,
              bottom: emojiPanelBottomOffset,
              zIndex: 25,
            }}
            onMouseDownCapture={handleEmojiMouseDown}
            onPointerDownCapture={handleEmojiMouseDown}
          >
            <View
              ref={emojiPickerPanelRef}
              style={{
                width: emojiPickerLayout.panelWidth,
                height: emojiPickerLayout.panelHeight,
                maxWidth: "100vw",
                maxHeight: "100vh",
                borderRadius: 14,
                borderWidth: 1,
                borderColor: Colors.borderColor,
                backgroundColor: Colors.whiteColor,
                overflow: "hidden",
                boxSizing: "border-box",
              }}
              onMouseDownCapture={handleEmojiMouseDown}
              onPointerDownCapture={handleEmojiMouseDown}
            >
              <View
                ref={emojiPickerMountRef}
                style={{ width: "100%", height: "100%", overflow: "hidden" }}
                onMouseDownCapture={handleEmojiMouseDown}
                onPointerDownCapture={handleEmojiMouseDown}
              >
                {React.createElement(EmojiMartPicker, {
                  data: emojiMartData,
                  onEmojiSelect: handleEmojiSelect,
                  autoFocus: false,
                  locale: pickerLocale,
                  theme: "light",
                  dynamicWidth: false,
                  perLine: emojiPickerLayout.perLine,
                  emojiButtonSize: emojiPickerLayout.emojiButtonSize,
                  previewPosition: "none",
                  navPosition: "top",
                  searchPosition: "sticky",
                  skinTonePosition: "search",
                  maxFrequentRows: 1,
                })}
              </View>
            </View>
          </View>
        )}
        <View
          style={{
            backgroundColor: Colors.lightGrayColor,
            borderTopColor: Colors.borderColor,
            borderTopWidth: 0.1,
            paddingHorizontal: fullScreen ? 4 : 0,
          }}
        >
          {/* Reply to Message */}
          {chatManager.selectedReplyToId && (
            <ReplyToMessageView client={client} />
          )}

          <View
            style={{
              flexDirection: "row",
              width: "100%",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-end",
                paddingVertical: 8,
                paddingHorizontal: 10,
                flex: 1,
              }}
            >
              {shouldShowAppEmojiButton && (
                <View
                  ref={emojiToggleButtonRef}
                  nativeID="chat-emoji-toggle-button"
                >
                  <TouchableOpacity
                    onPress={handleEmojiButtonPress}
                    onMouseDown={handleEmojiMouseDown}
                    onPointerDown={handleEmojiMouseDown}
                    accessibilityRole="button"
                    accessibilityLabel={
                      showEmojiPicker
                        ? t("closeEmojiPicker-string")
                        : t("openEmojiPicker-string")
                    }
                    style={{
                      alignSelf: "flex-end",
                      alignItems: "center",
                      justifyContent: "center",
                      width: isMobile ? 32 : 30,
                      height: isMobile ? 36 : 34,
                      marginRight: 6,
                    }}
                  >
                    <Ionicons
                      name={showEmojiPicker ? "happy" : "happy-outline"}
                      size={22}
                      color={Colors.blackColor}
                    />
                  </TouchableOpacity>
                </View>
              )}
              {/* Selected Image Preview */}
              {selectedImage && (
                <View
                  style={{
                    position: "relative",
                    maxWidth: isMobile ? 36 : 34,
                    maxHeight: isMobile ? 36 : 34,
                    marginRight: 8,
                    alignItems: "center",
                  }}
                >
                  <View
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                    }}
                  >
                    <TouchableOpacity
                      onPress={() => {
                        clearSelectedImage();
                      }}
                      style={{
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Ionicons
                        name="trash-outline"
                        size={20}
                        color={Colors.blackColor}
                      />
                    </TouchableOpacity>
                  </View>
                  <View
                    style={{
                      opacity: 0.5,
                      maxWidth: 40,
                      overflow: "hidden",
                      borderRadius: 8,
                      alignSelf: "center",
                      alignItems: "center",
                      flexShrink: 1,
                      minWidth: 24,
                    }}
                  >
                    <ImageComponent
                      imageURL={selectedImage.previewUrl}
                      height="100%"
                      small
                    />
                  </View>
                </View>
              )}
              {/* Text Input */}
              <View
                ref={inputContainerRef}
                style={{
                  flex: 1,
                  marginRight: 8,
                  borderRadius: 14,
                  borderWidth: 1,
                  borderColor: Colors.borderColor,
                  backgroundColor: Colors.whiteColor,
                  minHeight: 34,
                  maxHeight: 107,
                  flexShrink: 1,
                  overflow: "hidden",
                }}
              >
                <TextareaAutosize
                  ref={inputRef}
                  value={client.chatText}
                  onChange={(event) => {
                    if (!audioManager.isRecording) {
                      const newText =
                        event?.currentTarget?.value ??
                        event?.target?.value ??
                        "";
                      const limitedText = limitChatMessageText(newText);
                      client.setProperty("chatText", limitedText);
                      selectionRef.current = getTextareaSelection(
                        event?.currentTarget,
                        limitedText,
                      );
                      setForcedSelection(undefined);
                    }
                  }}
                  onSelect={(event) => {
                    const nextSelection = getTextareaSelection(
                      event?.currentTarget,
                      client.chatText,
                    );
                    selectionRef.current = nextSelection;

                    if (
                      shouldClearForcedSelection(forcedSelection, nextSelection)
                    ) {
                      setForcedSelection(undefined);
                    }
                  }}
                  onKeyDown={(event) => {
                    const keyAction = getChatInputKeyAction({
                      key: event?.key,
                      shiftKey: event?.shiftKey,
                      isRecording: audioManager.isRecording,
                      chatText: client.chatText,
                    });

                    if (keyAction === ChatInputKeyAction.SEND) {
                      event.preventDefault?.();
                      sendMessage();
                      setTimeout(() => {
                        inputRef.current?.focus();
                      }, 80);
                      return;
                    }

                    if (keyAction === ChatInputKeyAction.CLOSE_CHAT) {
                      event.preventDefault?.();
                      closeChatPanel();
                    }
                  }}
                  onPaste={handlePasteImage}
                  placeholder={t("messagePlaceholder-string")}
                  className="chat-input-textarea"
                  minRows={1}
                  style={{
                    width: "100%",
                    resize: "none",
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    border: "none",
                    outline: "none",
                    fontWeight: "400",
                    fontSize: inputFontSize,
                    color: Colors.blackColor,
                    backgroundColor: "transparent",
                    paddingTop: inputVerticalPadding,
                    paddingBottom: inputVerticalPadding,
                    paddingLeft: 8,
                    paddingRight: 8,
                    margin: 0,
                    boxSizing: "border-box",
                    lineHeight: `${inputLineHeight}px`,
                    maxHeight: 107,
                    overflowY: "auto",
                  }}
                  maxLength={CHAT_MESSAGE_TEXT_MAX_LENGTH}
                  autoCorrect="off"
                  spellCheck="false"
                />
              </View>

              {/* Send Button or Image Picker */}
              {client.chatText.trim() !== "" || selectedImage !== null ? (
                <SendMessageButton />
              ) : (
                <TouchableOpacity
                  onPress={pickImage}
                  accessibilityRole="button"
                  accessibilityLabel={t("addImageButtonLabel-string")}
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    // flex: 1,
                  }}
                >
                  <Ionicons
                    name="image-outline"
                    size={24}
                    color={Colors.blackColor}
                    style={{ paddingLeft: 10 }}
                  />
                </TouchableOpacity>
              )}
            </View>

            {audioManager.isRecording && (
              <View
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 40,
                  bottom: 0,
                  backgroundColor: Colors.lightGrayColor,
                  zIndex: audioManager.isRecording ? 10 : 0,
                }}
              >
                <RecordingInfoView />
              </View>
            )}
            {!(client.chatText.trim() !== "" || selectedImage !== null) && (
              <DraggableMicrophone client={client} />
            )}
          </View>
        </View>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageInputChange}
        />
      </Animated.View>
    );
  },
);

const ReplyToMessageView = ({ client }) => {
  const chatManager = ChatManager.getInstance();
  const { t } = useTranslation();

  const replyToMessage = client.messages.find(
    (msg) => msg.id === chatManager.selectedReplyToId,
  );

  if (!replyToMessage) return null;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        height: 40,
        backgroundColor: Colors.lightGrayColor,
        width: "100%",
      }}
    >
      {/* Left Indicator */}
      <View
        style={{
          width: 4,
          height: 36,
          backgroundColor: "#808080",
          borderRadius: 2,
        }}
      />

      {/* Reply Message Info */}
      <View style={{ flex: 1, paddingHorizontal: 8 }}>
        <Text
          style={{
            fontSize: 14,
            fontWeight: "600",
            color: "#808080",
            marginBottom: 1,
          }}
        >
          {replyToMessage.isFromCoach ? "Coach" : "You"}
        </Text>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {/* Audio Message */}
          {replyToMessage.type === "audio" && (
            <>
              <Ionicons
                name="mic-outline"
                size={12}
                color="gray"
                style={{ marginRight: 4 }}
              />
              <Text style={{ fontSize: 12, color: "gray", flexShrink: 1 }}>
                {audioFormatTime(replyToMessage.totalTime)}
              </Text>
            </>
          )}

          {/* Image Message */}
          {replyToMessage.type === "image" && (
            <>
              <Ionicons
                name="camera-outline"
                size={12}
                color="gray"
                style={{ marginRight: 4 }}
              />
              <Text
                style={{
                  fontSize: 12,
                  color: "gray",
                  flexShrink: 1,
                  width: "100%",
                }}
                numberOfLines={1}
              >
                {replyToMessage.text || t("photo-string")}
              </Text>
            </>
          )}

          {/* Video Message */}
          {replyToMessage.type === "video" && (
            <>
              <Ionicons
                name="videocam-outline"
                size={12}
                color="gray"
                style={{ marginRight: 4 }}
              />
              <Text style={{ fontSize: 12, color: "gray", flexShrink: 1 }}>
                {replyToMessage.totalTime
                  ? `${t("video-string")} - ${Math.floor(
                      replyToMessage.totalTime,
                    )}s`
                  : t("video-string")}
              </Text>
            </>
          )}

          {/* Text Message */}
          {replyToMessage.type === "text" && (
            <Text
              style={{
                fontSize: 12,
                color: "gray",
                flexShrink: 1,
                width: "100%",
              }}
              numberOfLines={1}
            >
              {replyToMessage.text || t("photo-string")}
            </Text>
          )}
        </View>
      </View>

      {/* Close Button */}
      <TouchableOpacity
        onPress={() => {
          chatManager.setProperty("selectedReplyToId", null);
        }}
      >
        <Ionicons
          name="close-circle"
          size={20}
          color="gray"
          style={{ paddingRight: 8 }}
        />
      </TouchableOpacity>
    </View>
  );
};

const DraggableMicrophone = observer(({ client }) => {
  const audioManager = AudioManager.getInstance();
  const isRecording = audioManager.isRecording;

  /* ANIMATIONS */
  const animatedScale = useRef(new Animated.Value(1)).current;
  const lockedScale = useRef(new Animated.Value(1)).current;

  // Animate the outer pulsating circle.
  const pulsate = () => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(animatedScale, {
          toValue: 1.25,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(animatedScale, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
      ]),
    ).start();
  };

  // Animate locked indicator scale when locked.
  useEffect(() => {
    if (isRecording) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(lockedScale, {
            toValue: 1.2,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(lockedScale, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
        ]),
      ).start();
    } else {
      lockedScale.setValue(1);
    }
  }, [isRecording, lockedScale]);

  return (
    <View
      style={{
        alignItems: "center",
        justifyContent: "center",
        paddingRight: isRecording ? 10 : 8,
        marginLeft: -2,
        zIndex: 11,
      }}
    >
      {/* Outer container for fixed circle & pulsating circle */}
      <View
        style={{
          position: "relative",
          width: 34,
          height: 34,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isRecording && (
          <Animated.View
            style={{
              position: "absolute",
              width: 34,
              height: 34,
              borderRadius: 20,
              backgroundColor: "red",
              opacity: 0.3,
              transform: [{ scale: animatedScale }],
              zIndex: -1,
            }}
          />
        )}

        <TouchableOpacity
          activeOpacity={1}
          style={{
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            alignSelf: "center",
          }}
          onPress={() => {
            if (!isRecording) {
              audioManager.startRecording();
              pulsate();
            } else {
              audioManager.stopRecording(client);
            }
          }}
        >
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 20,
              backgroundColor: isRecording ? "rgb(181, 50, 40)" : "transparent",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 0,
              paddingTop: isRecording ? 2 : 0,
              paddingRight: isRecording ? 2 : 0,
            }}
          >
            <Ionicons
              name={isRecording ? "paper-plane" : "mic"}
              size={24}
              color={isRecording ? Colors.whiteColor : Colors.blackColor}
              style={{ opacity: 1, zIndex: 2 }}
            />
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
});

const RecordingInfoView = observer(() => {
  const audioManager = AudioManager.getInstance();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollToEnd({ animated: true });
    }
  }, [audioManager.soundSamples.length]);

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        flexGrow: 1,
        paddingHorizontal: 16,
        height: 56,
      }}
    >
      {/* Trash Button */}
      <TouchableOpacity
        onPress={() => audioManager.deleteRecording()}
        style={{
          marginRight: 8,
          marginBottom: 3,
        }}
      >
        <Ionicons name="trash-outline" size={24} color={Colors.redColor} />
      </TouchableOpacity>

      <Text
        style={{
          fontSize: 18,
          color: Colors.blackColor,
          marginLeft: 8,
          marginBottom: 3,
        }}
      >
        {`${Math.floor(audioManager.recordingTime / 60)}:${String(
          Math.floor(audioManager.recordingTime % 60),
        ).padStart(2, "0")}`}
      </Text>
      <Observer>
        {() => (
          <View style={{ flex: 1, overflow: "hidden", marginLeft: 8 }}>
            <ScrollView
              ref={scrollRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ width: "100%", paddingHorizontal: 4 }}
              contentContainerStyle={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {audioManager.soundSamples.map((sample, index) => (
                <View
                  key={index}
                  style={{
                    width: 3,
                    height: Math.max(5, sample * 28),
                    backgroundColor: Colors.blackColor,
                    borderRadius: 2,
                    marginHorizontal: 1,
                  }}
                />
              ))}
            </ScrollView>
          </View>
        )}
      </Observer>
    </View>
  );
});

export default ChatInputView;
